import type { ReactNode } from "react";

import { cn } from "../cn.js";

export type StorageLevel = "ok" | "informational" | "warning" | "critical";

export interface StorageMeterProps {
	usedBytes: number;
	totalBytes: number;
	level: StorageLevel;
	/** Pre-formatted by `@bafe/validation`, e.g. "17.5 GB of 25 GB used". */
	summary: string;
	/**
	 * Counts are informational only. They are rendered as plain facts, never
	 * as an entitlement such as "3 of 5 mailboxes used" (spec §78A.12).
	 */
	mailboxCount?: number;
	domainCount?: number;
	action?: ReactNode;
	className?: string;
}

const LEVEL_BAR: Record<StorageLevel, string> = {
	ok: "bg-(--color-brand-500)",
	informational: "bg-(--color-brand-500)",
	warning: "bg-(--color-caution-500)",
	critical: "bg-(--color-critical-500)",
};

const LEVEL_MESSAGE: Record<StorageLevel, string | null> = {
	ok: null,
	informational: null,
	warning: "You are close to your storage limit.",
	critical: "Your account has reached its storage limit.",
};

/**
 * The account storage meter.
 *
 * This is the customer's primary entitlement display: Bafe sells a shared
 * storage pool, so the pool is what the product measures.
 */
export function StorageMeter({
	usedBytes,
	totalBytes,
	level,
	summary,
	mailboxCount,
	domainCount,
	action,
	className,
}: StorageMeterProps) {
	const percent = totalBytes > 0 ? Math.min((usedBytes / totalBytes) * 100, 100) : 100;
	const message = LEVEL_MESSAGE[level];

	const counts = [
		mailboxCount !== undefined
			? `${mailboxCount} ${mailboxCount === 1 ? "email account" : "email accounts"}`
			: null,
		domainCount !== undefined ? `${domainCount} ${domainCount === 1 ? "domain" : "domains"}` : null,
	].filter(Boolean);

	return (
		<div className={cn("flex flex-col gap-2.5", className)}>
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<p className="text-sm font-medium text-(--text-primary)">{summary}</p>
				{action}
			</div>

			<div
				role="progressbar"
				aria-valuenow={Math.round(percent)}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-label={summary}
				className="h-1.5 w-full overflow-hidden rounded-full bg-(--surface-sunken)"
			>
				<div
					className={cn("h-full rounded-full transition-[width] duration-500", LEVEL_BAR[level])}
					style={{ width: `${percent}%` }}
				/>
			</div>

			{counts.length > 0 && <p className="text-xs text-(--text-muted)">{counts.join(" · ")}</p>}

			{message && (
				<p
					className={cn(
						"text-xs",
						level === "critical" ? "text-(--color-critical-700)" : "text-(--color-caution-700)",
					)}
				>
					{message}
				</p>
			)}
		</div>
	);
}
